import { useEffect, useState } from "react";
import {
  TraitContent,
  TraitPlasticity,
} from "@dimensional-engineering/dimensional-models";
import { Roboto_Mono } from "next/font/google";

import styles from "./AboutThisTrait.module.css";

import archetypeJsonData from "../../../../assets/traitInfo/archetypes.json";
import elementJsonData from "../../../../assets/traitInfo/elements.json";
import patternJsonData from "../../../../assets/traitInfo/patterns.json";
import { natureData } from "@/assets/traitInfo/natureData";
import { traitsMapper } from "../../shared/traitsMap";
import {
  archetypeMapper,
  patternGroupMapper,
} from "@/assets/traitInfo/dimensionData";
import InformationIcon from "@/components/premium/reports/blindspot/ReportAccuracy/InformationIcon";
import { GeneralModalArrayParagraph } from "@/components/shared/GeneralModal/GeneralModal";
import { useModalContext } from "@/context/ModalContext";

type Props = {
  traitContent: TraitContent;
  archetypeGroupSlug?: string;
  dimensionSlug?: string;
  mixedDimensions?: boolean;
  patternGroupSlug?: string | undefined | null;
  usersWithArchetypeNumber?: number | null | undefined;
  plasticity?: TraitPlasticity | null | undefined;
};

const font = Roboto_Mono({ subsets: ["latin"] });

export default function AboutThisTrait(props: Props) {
  const rows = useGetRowsData(props);

  const { setModalComponent } = useModalContext();

  const mapped = rows.map((row, index) => {
    if (row.name === "Fluidity") {
      return (
        <div
          onClick={() => {
            setModalComponent(
              <GeneralModalArrayParagraph
                title="Trait Fluidity"
                body={[
                  "Some aspects of who we are change very slow, if at all. Other aspects can change on an almost daily basis. We call this feature of traits fluidity.",
                  "Our mood, for instance, has high fluidity. It can change from day to day and sometimes moment to moment.",
                  "Our strengths, on the other hand, have a medium level of fluidity, meaning they evolve with our circumstances. For instance, people thrown into leadership positions can improve their leadership abilities.",
                  "Other “core” personality traits like Extroversion tend to have low fluidity. They evolve very slowly, usually over the course of years. Major life events and general maturation can change our stable traits.",
                ]}
              />
            );
          }}
          className={`${styles.row} ${font.className}`}
          key={row.name}
        >
          <p>{row.name}</p>
          <p>
            <span
              style={
                props.plasticity === TraitPlasticity.low
                  ? { color: "#E6E1DF" }
                  : { color: "#55504F" }
              }
            >
              Low
            </span>
            /
            <span
              style={
                props.plasticity === TraitPlasticity.medium
                  ? { color: "#E6E1DF" }
                  : { color: "#55504F" }
              }
            >
              Medium
            </span>
            /
            <span
              style={
                props.plasticity === TraitPlasticity.high
                  ? { color: "#E6E1DF" }
                  : { color: "#55504F" }
              }
            >
              High
            </span>
            <span className={styles.dimensionIconDiv}>
              <InformationIcon />
            </span>
          </p>
        </div>
      );
    }
    return (
      <div className={`${styles.row} ${font.className}`} key={row.name}>
        <p>{row.name}</p>
        <p
          className={
            row.value === "UltraRare"
              ? styles.ultraRare
              : row.value === "Rare"
              ? styles.rare
              : ""
          }
        >
          {row.value}
        </p>
      </div>
    );
  });

  return (
    <div className={styles.parent}>
      <div className={styles.header}>About this trait</div>
      {mapped}
    </div>
  );
}

function useGetRowsData(props: Props) {
  const [rows, setRows] = useState<{ name: string; value: any }[]>([]);

  const mapper = {
    archetype: "Archetype",
    element: "Element",
    pattern: "Pattern",
  };

  useEffect(() => {
    setRows([]);
    if (props.traitContent?.traitType !== undefined) {
      setRows((current) => [
        ...current,
        ///@ts-ignore
        { name: "Type", value: mapper[props.traitContent?.traitType] },
      ]);
    }

    if (props.dimensionSlug) {
      if (props.mixedDimensions) {
        setRows((current) => [
          ...current,

          {
            name: "Dimension",
            ///@ts-ignore
            value: "Mix",
          },
        ]);
      } else {
        let dimensionSlug = traitsMapper[props.dimensionSlug];
        setRows((current) => [
          ...current,

          {
            name: "Dimension",
            ///@ts-ignore
            value: dimensionSlug,
          },
        ]);
      }
    }

    if (props.traitContent?.nature) {
      natureData.forEach((nat) => {
        if (nat.slug === props.traitContent.nature) {
          setRows((current) => [
            ...current,

            {
              name: "Nature",
              ///@ts-ignore
              value: nat.name,
            },
          ]);
        }
      });
    }

    if (props.plasticity) {
      setRows((current) => [
        ...current,
        {
          name: "Fluidity",
          ///@ts-ignore
          value: props.plasticity,
        },
      ]);
    }

    if (props.patternGroupSlug) {
      const groupSlugName = patternGroupMapper[props.patternGroupSlug];
      setRows((current) => [
        ...current,

        {
          name: "Pattern group",
          ///@ts-ignore
          value: groupSlugName,
        },
      ]);
    }

    if (props.archetypeGroupSlug) {
      setRows((current) => [
        ...current,
        {
          name: "Archetype Group",
          ///@ts-ignore
          value: archetypeMapper[props.archetypeGroupSlug],
        },
      ]);
    }

    if (props.traitContent?.oppositeTrait?.traitSlug) {
      let slug = props.traitContent?.oppositeTrait?.traitSlug;
      archetypeJsonData.forEach((arch) => {
        if (arch.slug === slug) {
          setRows((current) => [
            ...current,

            {
              name: "Opposite",
              ///@ts-ignore
              value: arch.name,
            },
          ]);
        }
      });
      elementJsonData.forEach((elem) => {
        if (elem.slug === slug) {
          setRows((current) => [
            ...current,

            {
              name: "Opposite",
              ///@ts-ignore
              value: elem.name,
            },
          ]);
        }
      });
      patternJsonData.forEach((patt) => {
        if (patt.slug === slug) {
          setRows((current) => [
            ...current,

            {
              name: "Opposite",
              ///@ts-ignore
              value: patt.name,
            },
          ]);
        }
      });
    }

    if (props.traitContent.averagePercentScore) {
      setRows((current) => [
        ...current,

        {
          name: "Average raw score",
          value: props.traitContent.averagePercentScore
            ? (props.traitContent.averagePercentScore * 100).toFixed()
            : "?",
        },
      ]);
    }

    if (props.usersWithArchetypeNumber) {
      setRows((current) => [
        ...current,

        {
          name: "Users with this Archetype",
          value: props.usersWithArchetypeNumber
            ? `${(props.usersWithArchetypeNumber * 100).toFixed()}%`
            : "?",
        },
      ]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.traitContent]);

  return rows;
}
